import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  BarChart3, LineChart, PieChart, Table, Search, Plus, Trash2, Edit3,
  Loader2, LayoutGrid, List, Clock, Database, Filter, Sparkles, Eye,
  ArrowLeft, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { VisualThumbnail } from '@/components/VisualThumbnail';
import { useTerms, getTerm } from '@/lib/branding';

const vizIcons = {
  bar_chart: BarChart3, line_chart: LineChart, area_chart: LineChart,
  pie_chart: PieChart, table: Table, kpi: BarChart3, pivot: Table,
  combo: BarChart3, scatter: BarChart3, funnel: BarChart3,
  waterfall: BarChart3, treemap: BarChart3, heatmap: BarChart3,
};

const vizLabels = {
  bar_chart: 'Bar Chart', line_chart: 'Line Chart', area_chart: 'Area Chart',
  pie_chart: 'Pie Chart', table: 'Table', kpi: 'KPI Card', pivot: 'Pivot Table',
  combo: 'Combo', scatter: 'Scatter', funnel: 'Funnel',
  waterfall: 'Waterfall', treemap: 'Treemap', heatmap: 'Heatmap',
};

export default function VisualLibraryPage() {
  useTerms();
  const navigate = useNavigate();
  const [visuals, setVisuals] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterModel, setFilterModel] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [usageCounts, setUsageCounts] = useState({});

  useEffect(() => {
    Promise.all([api.getVisuals().catch(() => []), api.getSemanticModels().catch(() => [])])
      .then(([v, m]) => {
        setVisuals(v); setModels(m);
        // Fetch usage counts for all visuals
        v.forEach(vis => {
          api.getVisualUsage(vis.visual_id).then(u => {
            setUsageCounts(prev => ({ ...prev, [vis.visual_id]: u.count || 0 }));
          }).catch(() => {});
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = visuals.filter(v => {
    if (searchQuery && !v.name?.toLowerCase().includes(searchQuery.toLowerCase()) && !v.description?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterModel !== 'all' && v.model_id !== filterModel) return false;
    if (filterType !== 'all' && v.viz_type !== filterType) return false;
    return true;
  });

  const handleCreate = async (selectedModelId) => {
    try {
      const v = await api.createVisual({ name: 'Untitled Visual', model_id: selectedModelId, viz_type: 'bar_chart' });
      setVisuals(prev => [v, ...prev]);
      toast.success(`${getTerm('visual_library', 'singular')} created`);
      navigate(`/visual-library/edit?id=${v.visual_id}`);
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteVisual(id);
      setVisuals(prev => prev.filter(v => v.visual_id !== id));
      setDeleteConfirm(null);
      toast.success(`${getTerm('visual_library', 'singular')} deleted`);
    } catch (err) { toast.error(err.message); }
  };

  const getModelName = (modelId) => models.find(m => m.model_id === modelId)?.name || 'Unknown';

  const uniqueTypes = [...new Set(visuals.map(v => v.viz_type))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="visual-library-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">{getTerm('visual_library')}</h1>
          <p className="text-sm text-muted-foreground mt-1">Reusable visualizations across dashboards and reports</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2" data-testid="create-visual-btn">
          <Plus className="w-4 h-4" /> New {getTerm('visual_library', 'singular')}
        </Button>
      </div>

      {/* Model Picker — shown when creating a new visual */}
      {createOpen && (
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <div className="max-w-2xl w-full">
            <div className="flex items-center gap-3 mb-6">
              <Button variant="ghost" size="icon" onClick={() => setCreateOpen(false)} className="rounded-lg w-8 h-8"><ArrowLeft className="w-4 h-4" /></Button>
              <div>
                <h1 className="text-2xl font-heading font-bold text-foreground">New {getTerm('visual_library', 'singular')}</h1>
                <p className="text-sm text-muted-foreground">Choose the semantic model this {getTerm('visual_library', 'singular', 'lower')} will be built against</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {models.map(m => (
                <button key={m.model_id} onClick={() => handleCreate(m.model_id)}
                  className="glass-card rounded-2xl p-5 text-left hover:border-primary/30 hover:shadow-[0_0_20px_rgba(59,130,246,0.15)] transition-all active:scale-[0.98] group"
                  data-testid={`pick-model-${m.model_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 group-hover:bg-indigo-500/20 transition-colors">
                      <Layers className="w-6 h-6 text-indigo-400" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-heading font-semibold text-foreground">{m.name}</h3>
                      {m.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{m.description}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge className={`text-[9px] ${m.status === 'published' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/15 text-amber-400 border-amber-500/20'}`}>{m.status}</Badge>
                      <span className="text-[10px] text-muted-foreground">v{m.version || '1.0'}</span>
                    </div>
                  </div>
                </button>
              ))}
              {models.length === 0 && (
                <div className="glass-card rounded-2xl p-8 text-center">
                  <p className="text-sm text-muted-foreground">No semantic models available. Create one first.</p>
                  <Button onClick={() => navigate('/semantic-models')} variant="outline" className="mt-3 rounded-lg text-xs">Go to Semantic Models</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {!createOpen && <>
      <div className="flex gap-3 mb-5">
        <Badge variant="secondary" className="gap-1.5 px-3 py-1">
          <BarChart3 className="w-3.5 h-3.5" /> {visuals.length} {getTerm('visual_library', 'singular')}s
        </Badge>
        <Badge variant="secondary" className="gap-1.5 px-3 py-1">
          <Database className="w-3.5 h-3.5" /> {new Set(visuals.map(v => v.model_id)).size} Models
        </Badge>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={`Search ${getTerm('visual_library', 'singular', 'lower')}s...`}
            className="pl-9"
            data-testid="visual-search"
          />
        </div>
        <Select value={filterModel} onValueChange={setFilterModel}>
          <SelectTrigger className="w-48" data-testid="filter-model">
            <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="All models" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Models</SelectItem>
            {models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40" data-testid="filter-type">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {uniqueTypes.map(t => <SelectItem key={t} value={t}>{vizLabels[t] || t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex border rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Visual Cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <BarChart3 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-lg font-medium text-muted-foreground">No {getTerm('visual_library', 'singular', 'lower')}s yet</p>
          <p className="text-sm text-muted-foreground/80 mt-1">
            {visuals.length > 0 ? 'Try adjusting your filters' : `Create your first ${getTerm('visual_library', 'singular', 'lower')} or save one from ${getTerm('ai_explore')}`}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(v => {
            return (
              <div key={v.visual_id} className="group border rounded-xl overflow-hidden hover:border-primary/30 transition-all hover:shadow-lg" data-testid={`visual-card-${v.visual_id}`}>
                <div className="h-32 bg-gradient-to-br from-muted/30 to-muted/10 flex items-center justify-center relative">
                  <VisualThumbnail visual={v} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <Button size="sm" variant="secondary" className="h-7 text-xs gap-1" onClick={() => navigate(`/visual-library/edit?id=${v.visual_id}`)}>
                      <Edit3 className="w-3 h-3" /> Edit
                    </Button>
                    <Button size="sm" variant="secondary" className="h-7 text-xs gap-1" onClick={() => navigate(`/visual-library/preview?id=${v.visual_id}`)}>
                      <Eye className="w-3 h-3" /> Preview
                    </Button>
                  </div>
                  <Badge className="absolute top-2 right-2 text-[9px]" variant="secondary">
                    {vizLabels[v.viz_type] || v.viz_type}
                  </Badge>
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-sm truncate">{v.name}</h3>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{v.description || 'No description'}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground/80">
                      <Database className="w-3 h-3" />
                      <span className="truncate max-w-[120px]">{getModelName(v.model_id)}</span>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground/80">
                      <Clock className="w-3 h-3" />
                      {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : ''}
                    </div>
                  </div>
                  {v.top_n?.enabled && (
                    <Badge variant="outline" className="text-[9px] mt-1.5">
                      {v.top_n.direction === 'bottom' ? 'Bottom' : 'Top'} {v.top_n.n}
                    </Badge>
                  )}
                  {(usageCounts[v.visual_id] || 0) > 0 && (
                    <Badge variant="outline" className="text-[9px] mt-1.5 text-emerald-500 border-emerald-500/30">
                      Used in {usageCounts[v.visual_id]} dashboard{usageCounts[v.visual_id] > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/20">
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Name</th>
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Type</th>
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Model</th>
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Fields</th>
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Updated</th>
                <th className="text-right text-xs font-medium text-muted-foreground px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => {
                const Icon = vizIcons[v.viz_type] || BarChart3;
                return (
                  <tr key={v.visual_id} className="border-b hover:bg-muted/10 cursor-pointer" onClick={() => navigate(`/visual-library/edit?id=${v.visual_id}`)}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{v.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{vizLabels[v.viz_type] || v.viz_type}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{getModelName(v.model_id)}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{(v.row_dims?.length || 0) + (v.value_measures?.length || 0)} fields</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{v.updated_at ? new Date(v.updated_at).toLocaleDateString() : ''}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); setDeleteConfirm(v.visual_id); }}>
                        <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>}

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {getTerm('visual_library', 'singular')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {(usageCounts[deleteConfirm] || 0) > 0
              ? `This visual is used in ${usageCounts[deleteConfirm]} dashboard${usageCounts[deleteConfirm] > 1 ? 's' : ''}. Deleting it will remove it from those dashboards.`
              : 'This visual will be removed from the library.'}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
